import { Badge } from "@/components/ui/badge";

/* ---------------------------------------------------------------------------
   Novedades — changelog del producto. Contenido estático (a mano, sin CMS).
   Cada entrada resume commits reales del repo (ver git log); actualizar este
   array cuando se cierre una tanda de trabajo presentable.

   REGLA: acá va lo VIGENTE, no todo lo que alguna vez existió. Una entrada que
   anuncia una pantalla que después se eliminó manda al usuario a buscar algo
   que no está, y eso es peor que no haberla anunciado. Cuando algo se da de
   baja se CORRIGE la entrada que lo prometía; esta página es el estado del
   producto, no su bitácora — la bitácora la lleva git.
--------------------------------------------------------------------------- */

interface NovedadEntry {
  version: string;
  fecha: string;
  titulo: string;
  descripcion: string;
  items: string[];
  tag: "success" | "info" | "warning";
}

const novedades: NovedadEntry[] = [
  {
    version: "v0.10.0",
    fecha: "28 de agosto de 2026",
    titulo: "Entras directo a tu trabajo",
    descripcion: "Al iniciar sesión la plataforma abre la lista de informes en proceso, sin una pantalla intermedia de accesos.",
    items: [
      "Informes en proceso es ahora la primera pantalla: se acabó el paso extra entre el ingreso y el trabajo.",
      "El menú lateral y el logo llevan a esa misma lista, así que volver al punto de partida es un solo clic.",
      "Los enlaces guardados a la antigua página de inicio siguen funcionando: llevan a la lista de informes.",
    ],
    tag: "success",
  },
  {
    version: "v0.9.0",
    fecha: "27 de agosto de 2026",
    titulo: "Una interfaz más compacta",
    descripcion: "La plataforma ajusta la densidad de toda la interfaz para que quepa más trabajo en pantalla.",
    items: [
      "El menú lateral entra completo en una sola columna, incluso con la letra del sistema ampliada.",
      "Toda la interfaz baja de densidad: más información visible sin perder legibilidad.",
      "Los textos y mensajes quedan homologados en un mismo tono, claro y neutro.",
      "El perfil del usuario se elige al crearlo, en vez de tener que volver después a su ficha.",
    ],
    tag: "success",
  },
  {
    version: "v0.8.0",
    fecha: "27 de agosto de 2026",
    titulo: "La aceptación del cliente pone el trabajo en marcha",
    descripcion: "La cotización deja de terminar en el envío: cuando el cliente acepta, el informe arranca y queda registrado por dónde entró esa aceptación.",
    items: [
      "El cliente acepta su cotización desde el enlace del correo, sin necesidad de tener una cuenta.",
      "La aceptación también se registra a mano, dejando constancia de quién la registró y por qué medio llegó.",
      "Al aceptar, el informe pasa a En proceso y sale el correo que le pide los antecedentes al cliente.",
      "La entrega se pacta en días hábiles y el plazo corre a contar de la visita de inspección, igual que lo dice el documento del cliente.",
      "Cada revisor ve y visa solo los informes que le corresponden; el Super Admin, todos.",
      "El número de informe del ERP es obligatorio al crear y al editar, para que ninguno quede desconectado de ese sistema.",
    ],
    tag: "success",
  },
  {
    version: "v0.7.0",
    fecha: "26 de agosto de 2026",
    titulo: "Asistencia de IA, correos con PDF y Feedback",
    descripcion: "Entra la primera capa de inteligencia artificial en la redacción, la cotización viaja al cliente como documento y se abre un canal para reportar lo que falle.",
    items: [
      "Asistencia de redacción con IA en los campos del informe: propone un texto y tú decides si lo tomas.",
      "Mantenedor de IA para ajustar el modelo y las instrucciones de cada campo sin esperar una nueva versión.",
      "La cotización se envía al cliente con su PDF adjunto.",
      "Los correos del informe salen por SendGrid y reintentan solos ante una falla pasajera.",
      "Tablero de Feedback para reportar sugerencias o problemas, con filtros y seguimiento del estado.",
      "Las fechas se muestran y se capturan en formato chileno.",
    ],
    tag: "success",
  },
  {
    version: "v0.6.0",
    fecha: "25 de agosto de 2026",
    titulo: "El informe de tasación, de punta a punta",
    descripcion: "El flujo central del negocio queda operativo: crear el informe, cotizarlo, mandarlo a revisión y visarlo.",
    items: [
      "Creación del informe con sus responsables, sus documentos y el número que lo cruza con el ERP.",
      "Informes en proceso: la cola de trabajo, con filtros, orden y paginación.",
      "Edición del informe, con aviso al salir si quedaron cambios sin guardar.",
      "Cotización del informe y envío a revisión, con vista previa del documento que recibe el cliente.",
      "Revisiones pendientes: la bandeja del revisor, con el visado de la cotización.",
      "Mantenedores de Contextos y de Revisión de informes.",
    ],
    tag: "success",
  },
  {
    version: "v0.5.0",
    fecha: "24 de agosto de 2026",
    titulo: "Administración de usuarios y catálogos",
    descripcion: "Los mantenedores dejan de ser una maqueta: las cuentas, los perfiles y los contenidos del informe se administran de verdad.",
    items: [
      "Mantenedor de Usuarios: alta con RUT chileno, cargo, perfil y estado de acceso.",
      "Bloquear una cuenta corta su sesión en el acto, sin esperar a que caduque.",
      "Mantenedor de Perfiles: qué pantallas abre cada perfil.",
      "Mantenedor de Contenidos: los ítems que componen el informe de tasación.",
      "Cada pantalla de administración queda reservada al perfil que corresponde.",
    ],
    tag: "success",
  },
  {
    version: "v0.4.0",
    fecha: "17 de julio de 2026",
    titulo: "Despliegue a producción vía registry",
    descripcion: "La infraestructura de prod queda operativa: imágenes publicadas y desplegadas desde el registry, con TLS automático.",
    items: [
      "Router de producción para el webapp con Traefik y certificados TLS (ACME).",
      "Imágenes de api y webapp publicadas y desplegadas desde el Container Registry de GitLab.",
      "Límites de recursos por contenedor para evitar caídas por falta de memoria (OOM).",
      "El webapp pasa a servirse en el dominio raíz.",
    ],
    tag: "info",
  },
  {
    version: "v0.3.0",
    fecha: "17 de julio de 2026",
    titulo: "Autenticación y recuperación de contraseña",
    descripcion: "Login con sesión persistente y flujo completo de recuperación de contraseña por email.",
    items: [
      "Login con cookies + CSRF de Sanctum y guard de rutas privadas.",
      "Recuperación de contraseña por correo (plantillas SendGrid).",
      "Recuperación transparente ante expiración de sesión, sin perder lo que estabas completando.",
    ],
    tag: "success",
  },
  {
    version: "v0.2.0",
    fecha: "16 de julio de 2026",
    titulo: "Design System aplicado",
    descripcion: "Toda la interfaz se reconstruyó sobre el Design System Consultek, con un catálogo vivo para validarlo.",
    items: [
      "Tokens, tipografía y componentes shadcn/ui adaptados a la identidad Consultek.",
      "Sidebar, header y layout general homologados en todas las páginas.",
      "Catálogo de componentes navegable en Design System.",
    ],
    tag: "success",
  },
  {
    version: "v0.1.0",
    fecha: "15 de julio de 2026",
    titulo: "Primer scaffold del webapp",
    descripcion: "Estructura inicial de la plataforma sobre React y Vite: la navegación, las cuentas y la base de pruebas sobre las que se construyó todo lo demás.",
    items: [
      "Rutas, navegación y layout general de la plataforma.",
      "Cuentas de usuario con invitación por correo y activación de la cuenta.",
      "Base de testing con Vitest y Testing Library.",
    ],
    tag: "info",
  },
];

const tagLabel: Record<NovedadEntry["tag"], string> = {
  success: "Feature",
  info: "Infra",
  warning: "Fix",
};

export function Novedades() {
  return (
    <div className="px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <div className="flex flex-col gap-6">
        {novedades.map((n, i) => (
          <div key={n.version} className="flex gap-5">
            <div className="flex flex-col items-center">
              <span className="mt-2 h-3 w-3 flex-none rounded-full bg-primary" />
              {i < novedades.length - 1 && <span className="mt-1 w-px flex-1 bg-border" />}
            </div>

            <div className="flex-1 rounded-3xl border bg-card p-6">
              <div className="flex flex-wrap items-center gap-2.5">
                <Badge variant={n.tag}>{tagLabel[n.tag]}</Badge>
                <span className="text-sm font-bold text-muted-foreground">{n.version}</span>
                <span className="text-sm text-muted-foreground">· {n.fecha}</span>
              </div>
              <h3 className="mt-2.5 text-lg font-extrabold tracking-tight">{n.titulo}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{n.descripcion}</p>
              <ul className="mt-3.5 flex flex-col gap-1.5">
                {n.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-foreground/90">
                    <span className="mt-[0.4375rem] h-1.5 w-1.5 flex-none rounded-full bg-muted-foreground/40" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
